// Which map objects are currently shown.
//
// Three things can hide an object, and they compose rather than override one
// another:
//
//   focus     the layout root, set by clicking into an app (setLayoutRoot)
//   roles     a role whose icon has been switched off in the filter panel
//   idle      nothing has been logged at or below it for a while
//
// The env filter is not here: environments are part of an object's path, so the
// server can be told not to send them at all, which is cheaper than receiving
// them and hiding them. Roles are filtered here instead because the panel toggles
// them by icon, and an object's icon only exists in its payload.
//
// The idle test keys off `lastEvents`, which `schema/role.js` writes on every
// counts event and propagates *up* the tree — so an entry on an app already means
// "this or anything below it", which is exactly the question being asked.

import map from "../map";
import logLevels from "../schema/logLevels";

// Types that report log activity of their own. A scope lives for seconds and
// carries no counts, and an external resource reports separately, so neither is
// subject to the idle rule — they follow whatever their ancestors do.
const IDLE_TYPES = {
  app: true,
  env: true,
  machine: true,
  process: true,
  role: true,
};

export const SOURCE_LEVEL = "level";
export const SOURCE_SCOPE = "scope";

export const visibility = {
  // null means everything; otherwise a set of role icons to show. Selecting none
  // is the same as selecting all — an empty map is never what was meant.
  roleEmojis: null,
  // null means never hide on idle; otherwise milliseconds.
  idleMs: 5 * 60 * 1000,
  // What counts as being alive: log entries at a chosen level or above, or any
  // scope having run beneath.
  source: SOURCE_SCOPE,
  // Index into logLevels, used when the source is log levels.
  logLevel: 0,
  // Health-check scopes are excluded by default, both from the map and from
  // counting as activity. Every service is polled constantly, so with them in,
  // "has had a scope beneath it recently" is true of nearly everything and the
  // filter stops telling you anything.
  showHealth: false,
};

// A scope whose summary is a health-check request.
export function isHealthSummary(summary) {
  return typeof summary === "string" && summary.indexOf("/health") === 0;
}

if (typeof window !== "undefined") {
  window.mapVisibility = visibility;
}

let onChanged = null;

export function setVisibilityChanged(fn) {
  onChanged = fn;
}

export function setRoleFilter(emojis) {
  visibility.roleEmojis = emojis;
  if (onChanged) onChanged();
}

export function setIdleFilter(idleMs, source, logLevel) {
  visibility.idleMs = idleMs;
  visibility.source = source;
  visibility.logLevel = logLevel;
  if (onChanged) onChanged();
}

export function setShowHealth(show) {
  visibility.showHealth = show;
  if (onChanged) onChanged();
}

function roleEmojiOf(obj) {
  if (!obj.data) return "";
  const parts = obj.data.split(":");
  return parts.length < 2 ? "" : parts[1];
}

// Most recent activity at or below `obj`.
//
// Nothing counts as active merely for existing. A branch stays hidden until it
// actually reports something, so a fresh page starts sparse and fills in as
// traffic arrives, rather than showing the whole estate and thinning out.
function lastActivity(obj) {
  let latest = 0;

  if (visibility.source === SOURCE_SCOPE) {
    // Two timestamps are kept as scopes arrive, so switching health checks in and
    // out takes effect on the history as well as on what comes next.
    const at = visibility.showHealth ? obj.lastScopeAt : obj.lastRealScopeAt;
    if (at > latest) latest = at;
    return latest;
  }

  const events = obj.lastEvents;
  if (!events) return latest;

  // Severity runs upwards through logLevels, and a warning is evidence of life
  // just as much as a trace is.
  for (let i = visibility.logLevel; i < logLevels.length; i++) {
    const at = events[logLevels[i].name];
    if (at > latest) latest = at;
  }
  return latest;
}

function hiddenBySelf(obj, now) {
  if (obj.type === "scope" && obj.isHealthScope && !visibility.showHealth) {
    return true;
  }

  if (obj.type === "role" && visibility.roleEmojis) {
    if (!visibility.roleEmojis[roleEmojiOf(obj)]) return true;
  }

  if (visibility.idleMs && IDLE_TYPES[obj.type]) {
    if (now - lastActivity(obj) > visibility.idleMs) return true;
  }

  return false;
}

// Hiding runs both ways.
//
// Downwards, because hiding an object hides everything under it: an app that has
// gone quiet should take its machines with it rather than leave them orbiting
// nothing.
//
// Upwards, because a branch that has nothing left in it should go too. Picking
// one role out otherwise left every process, machine, environment and app on the
// way back to the root still drawn, holding open a subtree with nothing in it.
//
// Both are memoised on a pass number so each object is decided once however the
// map is walked, which keeps this independent of the order `map.objects` happens
// to be in.
let pass = 0;

// Scopes come and go by the second and are not what a branch exists for, so a
// role with no scopes is still a role. Everything else is structure: if all of it
// has gone, its parent is an empty shell.
function isStructural(obj) {
  return obj.type !== "scope";
}

function resolveHidden(obj, now) {
  if (obj.hiddenPass === pass) return obj.hiddenResolved;
  obj.hiddenPass = pass;

  let hide = !!obj.focusHidden || hiddenBySelf(obj, now);
  if (!hide && obj.parent) hide = resolveHidden(obj.parent, now);

  obj.hiddenResolved = hide;
  return hide;
}

function resolveEmpty(obj, now) {
  if (obj.emptyPass === pass) return obj.emptyResolved;
  obj.emptyPass = pass;
  // Guards against a malformed tree looping; a well-formed one never re-enters.
  obj.emptyResolved = false;

  let empty = false;

  // The root always stands, however much is filtered out — collapsing it would
  // leave a blank screen rather than an empty estate.
  if (obj.parent) {
    let structural = false;
    let anyKept = false;

    for (let i = 0; i < obj.children.length; i++) {
      const child = obj.children[i];
      if (!isStructural(child)) continue;
      structural = true;
      if (!resolveHidden(child, now) && !resolveEmpty(child, now)) {
        anyKept = true;
        break;
      }
    }

    empty = structural && !anyKept;
  }

  obj.emptyResolved = empty;
  return empty;
}

// Recomputes `hidden` across the whole map. Returns true if anything changed, so
// the caller can decide whether the layout needs solving again.
export function applyVisibility() {
  const now = Date.now();
  let changed = false;
  pass++;

  for (const key in map.objects) {
    const obj = map.objects[key];
    const hide = resolveHidden(obj, now) || resolveEmpty(obj, now);

    if (obj.hidden !== hide) {
      obj.hidden = hide;
      if (obj.elem) obj.elem.attr("display", hide ? "none" : "");
      changed = true;
    }
  }

  return changed;
}
